import { Anchor } from '@vtex/admin-ui'
import { useDrawerSubForm, useTrack } from 'hooks'
import { useEffect, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  EMPTY_BUDGET_NOTIFICATION,
  Event,
  type BaseBudget,
  type BudgetNotificationSettings,
} from 'typings'
import { useAnalytics } from 'use-analytics'
import {
  CancelButton,
  DismissButton,
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerSubtitle,
  DrawerTitle,
  SaveButton,
  UseDrawerStateReturn,
} from '../../../Drawer'
import { CurrencyInput } from '../../CurrencyInput'
import { trackBudgetDrawerAction, trackBudgetDrawerView } from '../trackBudget'
import { AutoStopAlert } from './AutoStopAlert'
import { BudgetNotification } from './Notification'
import { BudgetAmount, EMPTY_BUDGET } from './constants'
import { messages } from './messages'
import { styles } from './styles'
import { validationSuite } from './validation'

type Props = {
  initialConfig: BaseBudget | undefined
  state: UseDrawerStateReturn
  onSave: (config: BaseBudget) => void
}

export function BudgetEditor(props: Props) {
  const { initialConfig, state, onSave } = props
  const { hide, open } = state

  const intl = useIntl()

  const { track } = useAnalytics()
  const { $t, formatNumber } = intl

  const config: BudgetAmount | BaseBudget = useMemo(() => {
    if (initialConfig === undefined) {
      return { ...EMPTY_BUDGET }
    }

    return {
      ...EMPTY_BUDGET,
      ...initialConfig,
      notification: {
        ...EMPTY_BUDGET_NOTIFICATION,
        ...initialConfig.notification,
      },
    }
  }, [initialConfig])

  const { drawerForm } = useDrawerSubForm({
    isOpen: open,
    config,
    validationFn: validationSuite(intl),
  })

  const [alertIsVisible, setAlertVisibility] = useState<boolean>(
    config.amount !== undefined
  )

  const [notificationOpen, setNotificationOpen] = useState(false)
  const [notificationDraft, setNotificationDraft] =
    useState<BudgetNotificationSettings | null>(null)

  useEffect(() => {
    if (!open) {
      setNotificationOpen(false)
      setNotificationDraft(null)
    }
  }, [open])

  useTrack({
    event: Event.BUDGET_NOTIFICATION_SETTINGS_CLICK,
    enable: notificationOpen && initialConfig?.notification === undefined,
    reset: !open,
  })

  useEffect(() => {
    if (open) {
      trackBudgetDrawerView({ config, track })
    }
  }, [open, track, config])

  const handleSave = async () => {
    const isValid = await drawerForm.trigger()

    if (isValid) {
      const values = drawerForm.getValues()

      const budgetData = {
        ...values,
        utilized: values.utilized ?? 0,
      }
      onSave(budgetData as BaseBudget)
      trackBudgetDrawerAction({
        event: Event.BUDGET_DRAWER_CONFIRM_CLICK,
        budgetValues: {
          initialValue: config.amount,
          currentValue: values.amount,
          utilized: values.utilized,
          initialNotification: config.notification,
          notification: values.notification,
        },
        track,
      })
      hide()
    }
  }
  const handleCancel = () => {
    const values = drawerForm.getValues()
    trackBudgetDrawerAction({
      event: Event.BUDGET_DRAWER_CANCEL_CLICK,
      budgetValues: {
        initialValue: config.amount,
        currentValue: values.amount,
        utilized: values.utilized,
        initialNotification: config.notification,
        notification: values.notification,
      },
      track,
    })
    hide()
  }
  return (
    <Drawer state={state}>
      <DrawerHeader>
        <DrawerTitle>
          <FormattedMessage {...messages.title} />
        </DrawerTitle>
        <DrawerSubtitle>
          <FormattedMessage
            {...messages.subtitle}
            values={{
              a: (content) => (
                <Anchor target="_blank" href={$t(messages.moreInformation)}>
                  {content}
                </Anchor>
              ),
            }}
          />
        </DrawerSubtitle>
        <DismissButton onHide={handleCancel} />
      </DrawerHeader>
      <DrawerContent>
        <div className={styles.content}>
          <CurrencyInput
            name="amount"
            state={drawerForm}
            label={$t(messages.label)}
            placeholder={formatNumber(0, {
              minimumFractionDigits: 2,
            })}
            helpText={$t(messages.helpText)}
            onChange={(event) => setAlertVisibility(event.target.value !== '')}
          />
          <AutoStopAlert hidden={!alertIsVisible} />
          <BudgetNotification
            name="notification"
            form={drawerForm}
            notificationDraft={notificationDraft}
            setNotificationDraft={setNotificationDraft}
            onOpen={() => setNotificationOpen(true)}
          />
        </div>
      </DrawerContent>
      <DrawerFooter>
        <CancelButton onClick={handleCancel} />
        <SaveButton onClick={handleSave} />
      </DrawerFooter>
    </Drawer>
  )
}
